import { logger } from '../../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { isGeoJSONSource } from '../map-engine.js'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'
import type { Feature } from '../../../schemas/feature.js'
import {
  buildLineStringFeature,
  buildPolygonFeature,
  buildPolygonPreviewFeature,
} from '../../../lib/drawing-geometry.js'
import type { RouteStrategy } from '../../../services/routing/route-planner-types.js'
import {
  routePath,
  previewToCursor,
  previewRoute,
  buildTrailGraph,
  type Graph,
  type SnapOpts,
  type LL,
} from '../../../services/routing/hybrid-snap-router.js'
import { demRouteWorker } from '../../../services/routing/dem-route-client.js'
import type { Feature as GeoFeature, LineString as GeoLineString } from 'geojson'

const log = logger.create('map')

function sameLngLat(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) < 1e-7 && Math.abs(a[1] - b[1]) < 1e-7
}

function haversineMeters(a: [number, number], b: [number, number]): number {
  const R = 6371000
  const toRad = (d: number): number => (d * Math.PI) / 180
  const dLat = toRad(b[1] - a[1])
  const dLng = toRad(b[0] - a[0])
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

/** Index of the path vertex roughly `distM` back from the end (toward the start). */
function spliceIndexBackFromEnd(path: [number, number][], distM: number): number {
  let acc = 0
  for (let i = path.length - 1; i > 0; i--) {
    acc += haversineMeters(path[i - 1], path[i])
    if (acc >= distM) return i - 1
  }
  return 0
}

function lineBbox(coords: [number, number][]): [number, number, number, number] {
  let w = Infinity,
    s = Infinity,
    e = -Infinity,
    n = -Infinity
  for (const [lng, lat] of coords) {
    if (lng < w) w = lng
    if (lng > e) e = lng
    if (lat < s) s = lat
    if (lat > n) n = lat
  }
  return [w, s, e, n]
}

function bboxIntersects(
  a: [number, number, number, number],
  b: [number, number, number, number]
): boolean {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]
}

/** A point-in-time snapshot of the in-progress drawing (anchors + routed gaps). */
interface DrawSnapshot {
  points: maplibregl.LngLat[]
  gaps: ([number, number][] | undefined)[]
}

export interface LinePolygonDrawingCallbacks {
  onFeatureCreated: (feature: Feature) => void
  onFeatureSelected: (featureId: string) => void
  updateContextMode: () => void
  getShapeComponent: (featureId: string) => { enterEditMode: () => void } | undefined
  /** A line/polygon is being drawn and has a committed draft feature the create
   *  toolbar can act on (style/label/etc.), or null when no draft is active. */
  onDraftChanged: (feature: Feature | null) => void
  /** A measure (ephemeral line) was finished: hands off its geometry so the
   *  caller can open the throwaway View. No feature is persisted. */
  onMeasureFinished: (geometry: GeoJSON.Geometry) => void
}

export class LinePolygonDrawingManager {
  private map: maplibregl.Map | undefined

  private drawingPoints: maplibregl.LngLat[] = []
  private isDrawing = false
  private isFinishing = false

  // Route-following: when set, each new line point is routed from the previous
  // anchor via the planner. drawingPoints stays the user's anchors; routedGaps[i]
  // is the routed path between anchor i and i+1 (provisional straight until the
  // async route resolves). The rendered/committed line is the stitched gaps.
  private followStrategy: RouteStrategy | null = null
  // Snap toggles for line routing (settable from the create toolbar mid-draw).
  // followStrategy is derived: set when either snap is on, null = freehand.
  private snapTrails = false
  private snapTerrain = false
  // True once the user explicitly sets the toggles, so the first-click auto-default
  // won't clobber a pre-point choice.
  private snapUserSet = false
  // Committed in-progress feature so the create toolbar's tools have something to
  // act on while drawing. Geometry is synced as anchors/routes change.
  private draftFeatureId: string | null = null
  private draftCreating = false
  // Measure mode: drawn like a line (anchors + snapping + temp preview) but it
  // never commits a draft feature; finish hands the geometry off for an
  // ephemeral View instead of persisting. Reset by cleanupTempDrawing().
  private measureMode = false
  private routedGaps: Array<Array<[number, number]>> = []
  private tempDrawingSourceId = 'temp-drawing'
  private tempDrawingFillLayerId = 'temp-drawing-fill'
  private tempDrawingStrokeLayerId = 'temp-drawing-stroke'
  private drawingHandleMarkers: maplibregl.Marker[] = []
  private mousePreviewSourceId = 'mouse-preview'
  private mousePreviewLayerId = 'mouse-preview-line'
  // Hover preview: cached visible lines (for cheap snap), a snap-point indicator,
  // and an rAF coalescer so we recompute at most once per frame.
  private previewFeatures: GeoFeature<GeoLineString>[] | null = null
  private previewGraph: Graph | null = null
  // Routable lines accumulated across the drawing session (keyed, deduped) so routing
  // reaches back to anchors that have panned off-screen. Pruned to the drawn region.
  private sessionLines = new Map<string, GeoFeature<GeoLineString>>()
  private snapIndicator: maplibregl.Marker | undefined
  private previewRaf: number | null = null
  // DEM hover preview (experimental): one in-flight request at a time, and the
  // last trail-snap result so a late DEM resolve doesn't overwrite a trail preview.
  private demPreviewBusy = false
  private lastPreviewOnTrail = false
  // The last DEM preview path for the current leg. We re-solve only a bounded tail
  // near the cursor and reuse this as the frozen prefix, so cost stays ~flat with
  // distance instead of growing with the whole leg.
  private lastDemPath: [number, number][] | null = null
  // Cursor we last initiated a DEM solve for — dedups identical positions so we never
  // re-run A* for a cursor we've already solved this leg.
  private lastDemCursor: [number, number] | null = null
  private static readonly DEM_PREVIEW_LOOKBACK_M = 700
  private pendingMousePos: maplibregl.LngLat | null = null

  // Drawing-session undo/redo. Each mutating op (place point, out-and-back, return)
  // snapshots the prior (anchors, routed-gaps) state; undo/redo step whole operations
  // (a Return is one step, not many points). Scoped to the active draw session.
  private undoStack: Array<DrawSnapshot> = []
  private redoStack: Array<DrawSnapshot> = []

  // Extending an existing line: the drawing session is seeded from that feature and
  // commits back to it on Finish (no draft is created, so Cancel leaves it untouched).
  // `extendReversed` = we loaded the line reversed to extend its start, so we reverse
  // the committed geometry back to preserve the stored direction.
  private extendingFeatureId: string | null = null
  private extendReversed = false
  // True after Extend is clicked but before an end is chosen: the toolbar (returns,
  // snap, undo/redo) is live and returns work, but placing points is blocked until the
  // user picks an end. A return, or an endpoint pick, "activates" the session.
  private extendAwaitingEnd = false

  private tempDrawingLayer:
    | { sourceId: string; layerId?: string; fillLayerId?: string; strokeLayerId?: string }
    | undefined
  private drawingHandles: { markers: maplibregl.Marker[] } | undefined
  private mousePreviewLayer: { sourceId: string; layerId: string } | undefined

  constructor(private callbacks: LinePolygonDrawingCallbacks) {}

  setMap(map: maplibregl.Map): void {
    this.map = map
    // Visible lines change when the viewport changes — drop the hover-preview caches.
    map.on('moveend', () => {
      this.previewFeatures = null
      this.previewGraph = null
    })
  }

  /** "On trail" tolerance as a fixed number of screen pixels (constant feel at any
   *  zoom), converted to meters for the current view. No metric floor — purely px. */
  private snapTolMeters(): number {
    const SNAP_PX = 12
    const z = this.map?.getZoom() ?? 14
    const lat = this.map?.getCenter().lat ?? 0
    const mppx = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, z)
    return mppx * SNAP_PX
  }

  /** If `ll` is within snap tolerance of a visible track (and trail-snap is on),
   *  return the snapped point on the track so the placed anchor lands exactly on it —
   *  guaranteeing routing treats it as on-trail (matching the hover preview). */
  snapToTrack(ll: maplibregl.LngLat): maplibregl.LngLat {
    if (!this.snapTrails || !this.map) return ll
    if (!this.previewFeatures) this.previewFeatures = this.gatherTrailFeatures()
    const r = previewToCursor(
      this.previewFeatures,
      [ll.lng, ll.lat],
      [ll.lng, ll.lat],
      this.snapTolMeters(),
      true
    )
    return r.snapPoint ? new maplibregl.LngLat(r.snapPoint[0], r.snapPoint[1]) : ll
  }

  get isCurrentlyDrawing(): boolean {
    return this.isDrawing
  }

  get drawingPointCount(): number {
    return this.drawingPoints.length
  }

  /** The most recently placed anchor (for "tap the last point to finish"). */
  get lastAnchor(): maplibregl.LngLat | undefined {
    return this.drawingPoints[this.drawingPoints.length - 1]
  }

  /** When true, line points are routed (trail/terrain) rather than drawn straight. */
  setFollowStrategy(strategy: RouteStrategy | null): void {
    this.followStrategy = strategy
  }

  get followStrategyValue(): RouteStrategy | null {
    return this.followStrategy
  }

  get snapTrailsOn(): boolean {
    return this.snapTrails
  }

  get snapTerrainOn(): boolean {
    return this.snapTerrain
  }

  /** Draw the next line as an ephemeral measure (no draft feature; finish opens
   *  a throwaway View). Toggled by the caller when the measure tool is picked. */
  setMeasureMode(on: boolean): void {
    this.measureMode = on
  }

  get isMeasuring(): boolean {
    return this.measureMode
  }

  /** True while a line/polygon is mid-creation with a committed draft feature. */
  get isCreatingShape(): boolean {
    return this.draftFeatureId !== null
  }

  /** Apply the default snap (from first-click trail detection) unless the user has
   *  already set the toggles for this shape. */
  setSnapDefault(trails: boolean, terrain: boolean): void {
    if (this.snapUserSet) return
    this.snapTrails = trails
    this.snapTerrain = terrain
    this.followStrategy = trails || terrain ? 'most-trail' : null
  }

  /** Set the snap toggles (explicit user action) and re-route the in-progress line. */
  setSnap(trails: boolean, terrain: boolean): void {
    this.snapTrails = trails
    this.snapTerrain = terrain
    this.snapUserSet = true
    this.lastDemPath = null
    this.lastDemCursor = null
    this.followStrategy = trails || terrain ? 'most-trail' : null
    if (!this.isDrawing) return
    if (this.followStrategy) {
      for (let gap = 0; gap < this.drawingPoints.length - 1; gap++) void this.routeGap(gap)
    } else {
      // Freehand: drop routed paths back to straight anchors.
      this.routedGaps = []
      this.renderTempShape()
      this.addDrawingHandles()
      void this.refreshDraft()
    }
  }

  /** Create the draft feature once the shape is valid, then keep its geometry synced. */
  private async refreshDraft(): Promise<void> {
    if (this.measureMode) {
      // Measuring keeps NO DB draft. Feed a synthetic in-memory line feature so the
      // create bar shows the full line UI (snap toggles, enabled Finish, undo/redo)
      // — it is never persisted. Below 2 points there's nothing to finish yet.
      const geom = this.drawingPoints.length >= 2 ? this.currentGeometry('line') : null
      this.callbacks.onDraftChanged(
        geom
          ? ({
              id: '__measure_draft__',
              type: 'line',
              name: 'Measurement',
              geometry: geom,
              properties: {},
            } as unknown as Feature)
          : null
      )
      return
    }
    // Extending an existing line uses no draft — the temp line is the preview and we
    // commit straight to the existing feature on finish.
    if (this.extendingFeatureId) return
    const tool = tripFeatureManager.getSelectedTool()
    if (!this.draftFeatureId && !this.draftCreating) {
      const valid =
        (tool === 'line' && this.drawingPoints.length >= 2) ||
        (tool === 'polygon' && this.drawingPoints.length >= 3)
      if (valid) {
        this.draftCreating = true // guard against duplicate creation on rapid clicks
        try {
          const f =
            tool === 'polygon'
              ? await tripFeatureManager.createPolygonFeature(
                  this.drawingPoints.map(p => ({ lat: p.lat, lng: p.lng }))
                )
              : await tripFeatureManager.createLineFeature(
                  this.stitchedCoords().map(([lng, lat]) => ({ lat, lng }))
                )
          // Drawing may have been cancelled while the create was in flight.
          if (!this.isDrawing) {
            void tripFeatureManager.deleteFeature(f.id)
          } else {
            this.draftFeatureId = f.id
            this.callbacks.onDraftChanged(f)
          }
        } catch (err) {
          log.warn('Failed to create draft feature while drawing:', err)
        } finally {
          this.draftCreating = false
        }
        return
      }
    }
    if (this.draftFeatureId) {
      const geom = this.currentGeometry(tool)
      if (geom) void tripFeatureManager.updateFeatureGeometry(this.draftFeatureId, geom)
    }
  }

  private currentGeometry(tool: string | null): GeoJSON.Geometry | null {
    if (tool === 'polygon') {
      const c = this.drawingPoints.map(p => [p.lng, p.lat] as [number, number])
      if (c.length < 3) return null
      c.push(c[0])
      return { type: 'Polygon', coordinates: [c] }
    }
    const coords = this.stitchedCoords()
    if (coords.length < 2) return null
    return { type: 'LineString', coordinates: coords }
  }

  /** Undo the most recent anchor point (line/polygon). */
  removeLastDrawingPoint(): void {
    if (this.drawingPoints.length === 0) return
    this.drawingPoints.pop()
    this.routedGaps.pop()
    const tool = tripFeatureManager.getSelectedTool()
    const minValid = tool === 'polygon' ? 3 : 2
    if (this.drawingPoints.length < minValid && this.draftFeatureId) {
      // Back below a valid shape — discard the committed draft, stay in drawing.
      const id = this.draftFeatureId
      this.draftFeatureId = null
      void tripFeatureManager.deleteFeature(id)
      this.callbacks.onDraftChanged(null)
    }
    if (this.drawingPoints.length === 0) {
      this.cleanupTempDrawing()
      this.callbacks.updateContextMode()
      return
    }
    this.renderTempShape()
    this.addDrawingHandles()
    void this.refreshDraft()
  }

  // ─────────────────────────── undo / redo ───────────────────────────

  get canUndo(): boolean {
    return this.undoStack.length > 0
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0
  }

  private cloneGaps(gaps: ([number, number][] | undefined)[]): ([number, number][] | undefined)[] {
    const out = new Array<[number, number][] | undefined>(gaps.length)
    for (let i = 0; i < gaps.length; i++) {
      const g = gaps[i]
      if (g) out[i] = g.map(c => [c[0], c[1]] as [number, number])
    }
    return out
  }

  private snapshot(): DrawSnapshot {
    // Anchors are never mutated in place (the array is only push/pop/replaced), so we
    // copy refs rather than reconstruct LngLats.
    return {
      points: [...this.drawingPoints],
      gaps: this.cloneGaps(this.routedGaps),
    }
  }

  /** Record the current state so the next mutating op can be undone. Clears redo. */
  private pushHistory(): void {
    this.undoStack.push(this.snapshot())
    this.redoStack = []
  }

  /** Replace the live drawing with a snapshot and re-render/sync the draft. */
  private applySnapshot(s: DrawSnapshot): void {
    this.drawingPoints = [...s.points]
    this.routedGaps = this.cloneGaps(s.gaps) as Array<Array<[number, number]>>
    this.lastDemPath = null
    this.lastDemCursor = null
    if (this.drawingPoints.length === 0) {
      if (this.draftFeatureId) {
        const id = this.draftFeatureId
        this.draftFeatureId = null
        void tripFeatureManager.deleteFeature(id)
        this.callbacks.onDraftChanged(null)
      }
      this.isDrawing = false
      this.cleanupTempDrawing()
      this.callbacks.updateContextMode()
      return
    }
    this.renderTempShape()
    this.addDrawingHandles()
    const tool = tripFeatureManager.getSelectedTool()
    const minValid = tool === 'polygon' ? 3 : 2
    if (this.drawingPoints.length < minValid && this.draftFeatureId) {
      const id = this.draftFeatureId
      this.draftFeatureId = null
      void tripFeatureManager.deleteFeature(id)
      this.callbacks.onDraftChanged(null)
    } else {
      void this.refreshDraft()
    }
    this.callbacks.updateContextMode()
  }

  /** Undo the last drawing operation (place point / out-and-back / return). */
  undo(): void {
    if (this.undoStack.length === 0) return
    this.redoStack.push(this.snapshot())
    this.applySnapshot(this.undoStack.pop()!)
  }

  /** Redo the last undone drawing operation. */
  redo(): void {
    if (this.redoStack.length === 0) return
    this.undoStack.push(this.snapshot())
    this.applySnapshot(this.redoStack.pop()!)
  }

  private clearHistory(): void {
    this.undoStack = []
    this.redoStack = []
  }

  /** Blank the hover-preview line + snap dot (e.g. after a return, where the old
   *  preview to the previous end is now stale). Next mousemove redraws from the new end. */
  private clearMousePreview(): void {
    this.lastDemPath = null
    this.lastDemCursor = null
    this.updateSnapIndicator(null)
    if (!this.map) return
    const source = this.map.getSource(this.mousePreviewSourceId)
    if (isGeoJSONSource(source)) {
      source.setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [] },
        properties: {},
      })
    }
  }

  // ─────────────────────────── returns (extend) ───────────────────────────

  /** Out-and-back: retrace the rendered path back to the start, staying in extend.
   *  Mirrors the anchors + routed gaps in reverse and appends them. */
  outAndBack(): void {
    // A return without an end pick acts from the end (closes the loop the same either way).
    if (this.extendAwaitingEnd) this.activateExtend('append')
    const n = this.drawingPoints.length
    if (n < 2) return
    this.pushHistory()
    // Append mirror anchors A(n-2)..A0 and the reverse of each corresponding gap.
    for (let k = 0; k < n - 1; k++) {
      // Reuse the existing anchor ref (anchors are immutable here) to mirror it.
      this.drawingPoints.push(this.drawingPoints[n - 2 - k])
      const srcGap = this.routedGaps[n - 2 - k]
      if (srcGap)
        this.routedGaps[n - 1 + k] = srcGap.map(c => [c[0], c[1]] as [number, number]).reverse()
    }
    this.renderTempLine(this.stitchedCoords())
    this.addDrawingHandles()
    this.clearMousePreview()
    void this.refreshDraft()
  }

  /** Return: best trail/line route from the working end back to the start, staying in
   *  extend. Falls back to out-and-back when there's no trail route home. */
  async returnHome(): Promise<void> {
    if (this.extendAwaitingEnd) this.activateExtend('append')
    const n = this.drawingPoints.length
    if (n < 2 || !this.map) return
    const end = this.drawingPoints[n - 1]
    const start = this.drawingPoints[0]
    try {
      if (!this.previewFeatures) this.previewFeatures = this.gatherTrailFeatures()
      if (!this.previewGraph) this.previewGraph = buildTrailGraph(this.previewFeatures)
      const path = await routePath(
        [
          [end.lng, end.lat],
          [start.lng, start.lat],
        ],
        {
          snapTrails: true,
          snapTerrain: false,
          lineFeatures: this.previewFeatures,
          snapTolM: this.snapTolMeters(),
          graph: this.previewGraph,
        }
      )
      // A 2-point result means routePath fell back to a straight line (no trail route
      // home) → mirror the drawn path instead.
      if (path.length <= 2) {
        this.outAndBack()
        return
      }
      this.pushHistory()
      this.drawingPoints.push(start)
      this.routedGaps[this.drawingPoints.length - 2] = path.map(
        c => [c[0], c[1]] as [number, number]
      )
      this.renderTempLine(this.stitchedCoords())
      this.addDrawingHandles()
      this.clearMousePreview()
      void this.refreshDraft()
    } catch (err) {
      log.warn('Return-home routing failed, using out-and-back:', err)
      this.outAndBack()
    }
  }

  // ─────────────────────────── extend an existing line ───────────────────────────

  get extendingFeature(): string | null {
    return this.extendingFeatureId
  }

  /** True between clicking Extend and choosing an end: returns work, point-adds are
   *  blocked (the caller gates taps on this). */
  get isAwaitingExtendEnd(): boolean {
    return this.extendAwaitingEnd
  }

  /** The seeded line's two ends [start, end] while awaiting an end pick (for hit-testing
   *  a tap against them), or null. */
  get extendEnds(): [maplibregl.LngLat, maplibregl.LngLat] | null {
    if (!this.extendAwaitingEnd || this.drawingPoints.length < 2) return null
    return [this.drawingPoints[0], this.drawingPoints[this.drawingPoints.length - 1]]
  }

  /** Begin an extend session (awaiting end pick). Seeds the line so returns can act on
   *  it, but does NOT render a temp line or accept points yet — the original shape stays
   *  the visual + shows endpoint handles until an end is chosen (or a return activates).
   *  Commits back to the feature on Finish; no draft, so Cancel is a no-op on it. */
  beginExtend(feature: Feature): void {
    if (!this.map) return
    const geom = feature.geometry
    if (geom.type !== 'LineString') return
    const coords = geom.coordinates as [number, number][]
    if (coords.length < 1) return
    this.extendingFeatureId = feature.id
    this.extendReversed = false
    this.extendAwaitingEnd = true
    this.drawingPoints = coords.map(c => new maplibregl.LngLat(c[0], c[1]))
    this.routedGaps = [] // existing vertices ARE the geometry; straight stitch reproduces it
    this.draftFeatureId = null
    this.isDrawing = false
    this.snapTrails = false
    this.snapTerrain = false
    this.snapUserSet = false
    this.followStrategy = null
    this.clearHistory()
    this.callbacks.updateContextMode()
  }

  /** Activate a seeded extend session from `direction`'s end: renders the temp line and
   *  starts accepting points. Reversing for 'prepend' so the working end is the array end
   *  (un-reversed on finish to preserve the stored direction). */
  private activateExtend(direction: 'append' | 'prepend'): void {
    if (!this.extendAwaitingEnd) return
    this.extendAwaitingEnd = false
    this.extendReversed = direction === 'prepend'
    if (this.extendReversed) this.drawingPoints.reverse()
    this.isDrawing = true
    this.renderTempLine(this.stitchedCoords())
    this.addDrawingHandles()
  }

  /** Pick the end to extend from (point-adding). Public entry from the endpoint handles. */
  chooseExtendEnd(direction: 'append' | 'prepend'): void {
    this.activateExtend(direction)
    this.callbacks.updateContextMode()
  }

  /** Commit an extend session back to the original feature's geometry and tear down the
   *  drawing state. Returns the feature id so the caller can refresh + re-select it
   *  (this is an edit of an existing feature, not a fresh create). */
  async finishExtend(): Promise<string | null> {
    if (this.isFinishing || !this.extendingFeatureId) return null
    this.isFinishing = true
    const featureId = this.extendingFeatureId
    try {
      const coords = this.stitchedCoords()
      const ordered = this.extendReversed ? [...coords].reverse() : coords
      if (ordered.length >= 2) {
        await tripFeatureManager.updateFeatureGeometry(featureId, {
          type: 'LineString',
          coordinates: ordered,
        })
      }
      this.cleanupTempDrawing() // also resets extendingFeatureId / extendReversed
      return featureId
    } catch (error) {
      log.error('Failed to finish line extend:', error)
      return null
    } finally {
      this.isFinishing = false
    }
  }

  /** Cancel an in-progress drawing, discarding any committed draft feature. */
  cancelDrawing(): void {
    if (this.draftFeatureId) {
      const id = this.draftFeatureId
      this.draftFeatureId = null
      void tripFeatureManager.deleteFeature(id)
    }
    this.cleanupTempDrawing()
    this.callbacks.onDraftChanged(null)
  }

  /** Re-render the temp geometry for the active tool (line stroke or polygon fill). */
  private renderTempShape(): void {
    const tool = tripFeatureManager.getSelectedTool()
    if (tool === 'polygon') this.renderTempPolygon()
    else this.renderTempLine(this.stitchedCoords())
  }

  addPointToLine(latLng: maplibregl.LngLat): void {
    this.pushHistory()
    this.drawingPoints.push(latLng)
    this.isDrawing = true
    // New anchor → new leg; the DEM preview prefix no longer applies.
    this.lastDemPath = null
    this.lastDemCursor = null

    if (this.followStrategy && this.drawingPoints.length >= 2) {
      const gap = this.drawingPoints.length - 2
      const a = this.drawingPoints[gap]
      // Provisional straight segment until the planner resolves the gap.
      this.routedGaps[gap] = [
        [a.lng, a.lat],
        [latLng.lng, latLng.lat],
      ]
      this.renderTempLine(this.stitchedCoords())
      void this.routeGap(gap)
    } else {
      this.renderTempLine(this.stitchedCoords())
    }

    this.addDrawingHandles()
    void this.refreshDraft()
  }

  /** Stitch the routed gaps (or straight anchors) into one coordinate list. */
  private stitchedCoords(): [number, number][] {
    if (this.drawingPoints.length === 1) {
      const p = this.drawingPoints[0]
      return [[p.lng, p.lat]]
    }
    const coords: [number, number][] = []
    for (let i = 0; i < this.drawingPoints.length - 1; i++) {
      const a = this.drawingPoints[i]
      const b = this.drawingPoints[i + 1]
      const seg = this.routedGaps[i] ?? [
        [a.lng, a.lat],
        [b.lng, b.lat],
      ]
      for (const c of seg) {
        const last = coords[coords.length - 1]
        if (last?.[0] !== c[0] || last[1] !== c[1]) coords.push(c)
      }
    }
    return coords
  }

  /** Route a single anchor gap, then re-render with the resolved path. */
  private async routeGap(gap: number): Promise<void> {
    const a = this.drawingPoints[gap]
    const b = this.drawingPoints[gap + 1]
    if (!a || !b || !this.followStrategy) return
    try {
      // Hybrid snap router (experiments): trail graph + z16 DEM least-cost, per segment.
      // "on trail" tolerance is ~8 screen px (zoom-derived), so it means genuinely on
      // the line — not tens of meters off (which would cause snap jaunts).
      // Reuse the hover preview's cached lines + noded graph instead of re-gathering
      // and re-noding on every click (that re-noding was the click lag).
      if (!this.previewFeatures) this.previewFeatures = this.gatherTrailFeatures()
      let graph: Graph | undefined
      if (this.snapTrails) {
        if (!this.previewGraph) this.previewGraph = buildTrailGraph(this.previewFeatures)
        graph = this.previewGraph
      }
      const opts: SnapOpts = {
        snapTrails: this.snapTrails,
        snapTerrain: this.snapTerrain,
        lineFeatures: this.previewFeatures,
        snapTolM: this.snapTolMeters(),
      }
      if (graph) opts.graph = graph
      const path = await routePath(
        [
          [a.lng, a.lat],
          [b.lng, b.lat],
        ],
        opts
      )
      // Drawing may have ended or this gap collapsed while routing — bail.
      if (!this.isDrawing || gap >= this.drawingPoints.length - 1) return
      if (path.length >= 2) this.routedGaps[gap] = path
      this.renderTempLine(this.stitchedCoords())
      this.addDrawingHandles()
      void this.refreshDraft()
    } catch (err) {
      log.warn('Route-follow gap failed, keeping straight segment:', err)
    }
  }

  /** All routable line geometry currently rendered (any layer), flattening
   *  MultiLineString. The solver routes over everything that's loaded/visible. */
  private gatherTrailFeatures(): GeoFeature<GeoLineString>[] {
    if (!this.map) return []
    // non-routable line types to skip (rivers/streams, contours, admin/boundaries,
    // labels, hillshade, building/landuse outlines). Everything else = a path.
    const SKIP =
      /water|river|stream|canal|ditch|lake|pond|contour|boundary|admin|border|label|hillshade|building|landuse|landcover|aeroway|pipeline|power|fence|barrier/i
    // Merge the current viewport's routable lines into the session accumulator.
    for (const f of this.map.queryRenderedFeatures()) {
      if (f.source === this.tempDrawingSourceId || f.source === this.mousePreviewSourceId) continue
      if (SKIP.test(`${f.sourceLayer ?? ''} ${f.layer?.id ?? ''} ${f.source ?? ''}`)) continue
      const g = f.geometry
      const lineSets: number[][][] =
        g?.type === 'LineString'
          ? [g.coordinates]
          : g?.type === 'MultiLineString'
            ? g.coordinates
            : []
      for (const coords of lineSets) {
        if (coords.length < 2) continue
        const k = `${f.source}:${f.id ?? ''}:${coords[0][0].toFixed(6)},${coords[0][1].toFixed(6)}:${coords.length}`
        if (!this.sessionLines.has(k)) {
          this.sessionLines.set(k, {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: coords as [number, number][] },
          })
        }
      }
    }
    // Prune to a region around the drawn path + current view (~a few miles) so the
    // accumulator stays bounded while still reaching anchors that panned off-screen.
    const region = this.routableRegionBbox()
    if (region) {
      for (const [k, feat] of this.sessionLines) {
        if (!bboxIntersects(lineBbox(feat.geometry.coordinates as [number, number][]), region)) {
          this.sessionLines.delete(k)
        }
      }
    }
    const out = Array.from(this.sessionLines.values())
    log.info(`[snap] gathered ${out.length} routable lines (session; reaches off-screen)`)
    return out
  }

  /** Bounding box of the current view ∪ all drawn anchors, expanded ~3.5 mi, so
   *  routing can reach back to an anchor that has scrolled off-screen. */
  private routableRegionBbox(): [number, number, number, number] | null {
    if (!this.map) return null
    const b = this.map.getBounds()
    let w = b.getWest(),
      s = b.getSouth(),
      e = b.getEast(),
      n = b.getNorth()
    for (const p of this.drawingPoints) {
      w = Math.min(w, p.lng)
      e = Math.max(e, p.lng)
      s = Math.min(s, p.lat)
      n = Math.max(n, p.lat)
    }
    const M = 0.05 // ~3.5 mi of latitude
    return [w - M, s - M, e + M, n + M]
  }

  /** Replace the temp line layer with the given coordinates. */
  private renderTempLine(coords: [number, number][]): void {
    if (!this.map) return
    if (this.map.getLayer(this.tempDrawingStrokeLayerId)) {
      this.map.removeLayer(this.tempDrawingStrokeLayerId)
    }
    if (this.map.getSource(this.tempDrawingSourceId)) {
      this.map.removeSource(this.tempDrawingSourceId)
    }
    if (coords.length < 2) {
      this.tempDrawingLayer = { sourceId: this.tempDrawingSourceId }
      return
    }
    this.map.addSource(this.tempDrawingSourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      },
    })
    this.map.addLayer({
      id: this.tempDrawingStrokeLayerId,
      type: 'line',
      source: this.tempDrawingSourceId,
      paint: { 'line-color': '#3f5a7a', 'line-width': 3, 'line-opacity': 0.8 },
    })
    this.tempDrawingLayer = {
      sourceId: this.tempDrawingSourceId,
      layerId: this.tempDrawingStrokeLayerId,
    }
  }

  addPointToPolygon(latLng: maplibregl.LngLat): void {
    this.drawingPoints.push(latLng)
    this.isDrawing = true
    this.renderTempPolygon()
    // Add handles for each point during drawing
    this.addDrawingHandles()
    void this.refreshDraft()
  }

  private renderTempPolygon(): void {
    // Remove previous temp drawing
    if (this.map) {
      if (this.map.getLayer(this.tempDrawingStrokeLayerId)) {
        this.map.removeLayer(this.tempDrawingStrokeLayerId)
      }
      if (this.map.getLayer(this.tempDrawingFillLayerId)) {
        this.map.removeLayer(this.tempDrawingFillLayerId)
      }
      if (this.map.getSource(this.tempDrawingSourceId)) {
        this.map.removeSource(this.tempDrawingSourceId)
      }
    }

    // Create polygon with solid edges for completed parts
    const polygonFeature = buildPolygonFeature(this.drawingPoints)

    if (this.map && polygonFeature) {
      this.map.addSource(this.tempDrawingSourceId, {
        type: 'geojson',
        data: polygonFeature,
      })

      this.map.addLayer({
        id: this.tempDrawingFillLayerId,
        type: 'fill',
        source: this.tempDrawingSourceId,
        paint: {
          'fill-color': '#3f5a7a',
          'fill-opacity': 0.2,
        },
      })

      this.map.addLayer({
        id: this.tempDrawingStrokeLayerId,
        type: 'line',
        source: this.tempDrawingSourceId,
        paint: {
          'line-color': '#3f5a7a',
          'line-width': 2,
          'line-opacity': 0.8,
        },
      })
    }

    this.tempDrawingLayer = {
      sourceId: this.tempDrawingSourceId,
      fillLayerId: this.tempDrawingFillLayerId,
      strokeLayerId: this.tempDrawingStrokeLayerId,
    }
  }

  private addDrawingHandles(): void {
    // Remove existing drawing handles
    if (this.drawingHandles) {
      this.drawingHandleMarkers.forEach(marker => marker.remove())
      this.drawingHandleMarkers = []
    }

    this.drawingHandles = { markers: [] }

    // Add handle for each drawing point
    this.drawingPoints.forEach((point, index) => {
      const isFirstPoint = index === 0
      const isLastPoint = index === this.drawingPoints.length - 1

      const handleElement = document.createElement('div')
      handleElement.style.width = '10px'
      handleElement.style.height = '10px'
      handleElement.style.cursor = 'grab'
      handleElement.style.zIndex = '2000'
      handleElement.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'

      if (isFirstPoint && this.drawingPoints.length > 2) {
        handleElement.style.background = '#3f5a7a'
        handleElement.style.border = '2px solid #3f5a7a'
        handleElement.style.borderRadius = '50%'
      } else if (isLastPoint && this.drawingPoints.length > 1) {
        handleElement.style.background = '#ef4444'
        handleElement.style.border = '2px solid #ef4444'
        handleElement.style.borderRadius = '50%'
      } else {
        handleElement.style.background = '#ffffff'
        handleElement.style.border = '1px solid #000000'
        handleElement.style.borderRadius = '0'
      }

      const marker = new maplibregl.Marker({
        element: handleElement,
        anchor: 'center',
      }).setLngLat([point.lng, point.lat])

      // (Finishing is via the Finish button — a click-to-finish handler here would
      // auto-finish on touch, where the new handle lands under the finger and catches
      // the tap's synthetic click.)

      // Add drag functionality for vertex adjustment during drawing
      this.setupDrawingHandleDragging(marker, index)

      if (this.map) {
        marker.addTo(this.map)
        this.drawingHandleMarkers.push(marker)
      }
    })

    this.drawingHandles.markers = this.drawingHandleMarkers
  }

  private setupDrawingHandleDragging(marker: maplibregl.Marker, vertexIndex: number): void {
    let isDragging = false
    const element = marker.getElement()

    element.addEventListener('mousedown', (e): void => {
      e.stopPropagation()
      e.preventDefault()
      isDragging = true

      if (this.map) {
        const mouseMoveHandler = (e: maplibregl.MapMouseEvent): void => {
          if (isDragging) {
            this.drawingPoints[vertexIndex] = e.lngLat
            marker.setLngLat([e.lngLat.lng, e.lngLat.lat])
            this.updateTempDrawingLayer()
          }
        }

        const mouseUpHandler = (e: maplibregl.MapMouseEvent): void => {
          e.originalEvent.stopPropagation()
          e.originalEvent.preventDefault()
          isDragging = false
          this.map!.off('mousemove', mouseMoveHandler)
          this.map!.off('mouseup', mouseUpHandler)
        }

        this.map.on('mousemove', mouseMoveHandler)
        this.map.on('mouseup', mouseUpHandler)
      }
    })
  }

  private updateTempDrawingLayer(): void {
    const tool = tripFeatureManager.getSelectedTool()

    if (this.tempDrawingLayer && this.map) {
      // Remove the old layers
      if (
        this.tempDrawingLayer.fillLayerId &&
        this.map.getLayer(this.tempDrawingLayer.fillLayerId)
      ) {
        this.map.removeLayer(this.tempDrawingLayer.fillLayerId)
      }
      if (
        this.tempDrawingLayer.strokeLayerId &&
        this.map.getLayer(this.tempDrawingLayer.strokeLayerId)
      ) {
        this.map.removeLayer(this.tempDrawingLayer.strokeLayerId)
      }
      if (this.tempDrawingLayer.layerId && this.map.getLayer(this.tempDrawingLayer.layerId)) {
        this.map.removeLayer(this.tempDrawingLayer.layerId)
      }
      if (this.map.getSource(this.tempDrawingLayer.sourceId)) {
        this.map.removeSource(this.tempDrawingLayer.sourceId)
      }

      if (tool === 'polygon') {
        const polygonFeature = buildPolygonFeature(this.drawingPoints)

        if (polygonFeature) {
          this.map.addSource(this.tempDrawingSourceId, {
            type: 'geojson',
            data: polygonFeature,
          })

          this.map.addLayer({
            id: this.tempDrawingFillLayerId,
            type: 'fill',
            source: this.tempDrawingSourceId,
            paint: { 'fill-color': '#3f5a7a', 'fill-opacity': 0.2 },
          })

          this.map.addLayer({
            id: this.tempDrawingStrokeLayerId,
            type: 'line',
            source: this.tempDrawingSourceId,
            paint: { 'line-color': '#3f5a7a', 'line-width': 2, 'line-opacity': 0.8 },
          })

          this.tempDrawingLayer = {
            sourceId: this.tempDrawingSourceId,
            fillLayerId: this.tempDrawingFillLayerId,
            strokeLayerId: this.tempDrawingStrokeLayerId,
          }
        }
      } else if (tool === 'line') {
        const lineFeature = buildLineStringFeature(this.drawingPoints)

        if (lineFeature) {
          this.map.addSource(this.tempDrawingSourceId, {
            type: 'geojson',
            data: lineFeature,
          })

          this.map.addLayer({
            id: this.tempDrawingStrokeLayerId,
            type: 'line',
            source: this.tempDrawingSourceId,
            paint: { 'line-color': '#3f5a7a', 'line-width': 3, 'line-opacity': 0.8 },
          })

          this.tempDrawingLayer = {
            sourceId: this.tempDrawingSourceId,
            layerId: this.tempDrawingStrokeLayerId,
          }
        }
      }
    }
  }

  async finishLineDrawing(): Promise<void> {
    if (this.isFinishing || this.drawingPoints.length < 2) {
      return
    }
    this.isFinishing = true

    // Measure: hand off the stitched geometry for an ephemeral View and leave the
    // temp line on the map as the measure's visual. No feature is persisted; no
    // edit mode. The temp drawing is cleared when the View is dismissed.
    if (this.measureMode) {
      try {
        const geom = this.currentGeometry('line')
        // Drop the vertex handles but keep the temp line drawn as the measure visual.
        if (this.drawingHandles) {
          this.drawingHandleMarkers.forEach(marker => marker.remove())
          this.drawingHandleMarkers = []
        }
        this.drawingHandles = undefined
        this.drawingPoints = []
        this.routedGaps = []
        this.clearHistory()
        this.isDrawing = false
        this.callbacks.onDraftChanged(null) // drop the synthetic create-bar feature
        if (geom) this.callbacks.onMeasureFinished(geom)
      } catch (error) {
        log.error('Failed to finish measure:', error)
      } finally {
        this.isFinishing = false
      }
      return
    }

    try {
      // The draft feature was already committed mid-draw; finalize its geometry and
      // hand it off as a normal created feature. (Fallback: create fresh if no draft.)
      let feature: Feature | undefined
      if (this.draftFeatureId) {
        const geom = this.currentGeometry('line')
        if (geom) await tripFeatureManager.updateFeatureGeometry(this.draftFeatureId, geom)
        feature = await tripFeatureManager.getFeature(this.draftFeatureId)
      }
      if (!feature) {
        const coords: [number, number][] = this.followStrategy
          ? this.stitchedCoords()
          : this.drawingPoints.map(p => [p.lng, p.lat])
        feature = await tripFeatureManager.createLineFeature(
          coords.map(([lng, lat]) => ({ lat, lng }))
        )
      }
      this.draftFeatureId = null
      this.callbacks.onDraftChanged(null)
      this.callbacks.onFeatureCreated(feature)
      this.cleanupTempDrawing()

      tripFeatureManager.selectFeature(feature.id)
      this.callbacks.updateContextMode()

      const shapeComponent = this.callbacks.getShapeComponent(feature.id)
      if (shapeComponent) {
        shapeComponent.enterEditMode()
      }

      log.debug('Line feature created and selected for editing:', feature.id)
    } catch (error) {
      log.error('Failed to create line feature:', error)
    } finally {
      this.isFinishing = false
    }
  }

  async finishPolygonDrawing(): Promise<void> {
    if (this.isFinishing || this.drawingPoints.length < 3) {
      return
    }
    this.isFinishing = true

    try {
      let feature: Feature | undefined
      if (this.draftFeatureId) {
        const geom = this.currentGeometry('polygon')
        if (geom) await tripFeatureManager.updateFeatureGeometry(this.draftFeatureId, geom)
        feature = await tripFeatureManager.getFeature(this.draftFeatureId)
      }
      if (!feature) {
        const points = this.drawingPoints.map(p => ({ lat: p.lat, lng: p.lng }))
        feature = await tripFeatureManager.createPolygonFeature(points)
      }
      this.draftFeatureId = null
      this.callbacks.onDraftChanged(null)
      this.callbacks.onFeatureCreated(feature)
      this.cleanupTempDrawing()

      tripFeatureManager.selectFeature(feature.id)
      this.callbacks.updateContextMode()

      const shapeComponent = this.callbacks.getShapeComponent(feature.id)
      if (shapeComponent) {
        shapeComponent.enterEditMode()
      }

      log.debug('Polygon feature created and selected for editing:', feature.id)
    } catch (error) {
      log.error('Failed to create polygon feature:', error)
    } finally {
      this.isFinishing = false
    }
  }

  updateMousePreview(mousePos: maplibregl.LngLat): void {
    if (this.drawingPoints.length === 0 || !this.map) {
      return
    }

    const tool = tripFeatureManager.getSelectedTool()

    if (tool === 'polygon' && this.drawingPoints.length >= 2) {
      const polyPreview = buildPolygonPreviewFeature(this.drawingPoints, mousePos)!

      const polySource = this.map.getSource(this.tempDrawingSourceId)
      if (isGeoJSONSource(polySource)) {
        polySource.setData(polyPreview)
      } else {
        this.map.addSource(this.tempDrawingSourceId, {
          type: 'geojson',
          data: polyPreview,
        })
        if (!this.map.getLayer(this.tempDrawingFillLayerId)) {
          this.map.addLayer({
            id: this.tempDrawingFillLayerId,
            type: 'fill',
            source: this.tempDrawingSourceId,
            paint: { 'fill-color': '#3f5a7a', 'fill-opacity': 0.2 },
          })
        }
        if (!this.map.getLayer(this.tempDrawingStrokeLayerId)) {
          this.map.addLayer({
            id: this.tempDrawingStrokeLayerId,
            type: 'line',
            source: this.tempDrawingSourceId,
            paint: { 'line-color': '#3f5a7a', 'line-width': 2, 'line-opacity': 0.8 },
          })
        }
        this.tempDrawingLayer = {
          sourceId: this.tempDrawingSourceId,
          fillLayerId: this.tempDrawingFillLayerId,
          strokeLayerId: this.tempDrawingStrokeLayerId,
        }
      }

      if (this.mousePreviewLayer) {
        if (this.map.getLayer(this.mousePreviewLayerId)) {
          this.map.removeLayer(this.mousePreviewLayerId)
        }
        if (this.map.getSource(this.mousePreviewSourceId)) {
          this.map.removeSource(this.mousePreviewSourceId)
        }
        this.mousePreviewLayer = undefined
      }
      return
    }

    // Coalesce mousemove → at most one recompute per animation frame.
    this.pendingMousePos = mousePos
    if (this.previewRaf !== null) return
    this.previewRaf = requestAnimationFrame(() => {
      this.previewRaf = null
      const pos = this.pendingMousePos
      if (pos && this.isDrawing) this.renderLinePreview(pos)
    })
  }

  private renderLinePreview(mousePos: maplibregl.LngLat): void {
    if (!this.map || this.drawingPoints.length === 0) return
    const lastPoint = this.drawingPoints[this.drawingPoints.length - 1]

    // Snap-aware preview: when over a track (and trail-snap is on), follow the
    // connected track via the cached graph all the way to the snapped point — the
    // same routing a click does. Off-trail/freehand → a plain segment to the cursor.
    let coords: [number, number][] = [
      [lastPoint.lng, lastPoint.lat],
      [mousePos.lng, mousePos.lat],
    ]
    let cursorOnTrail = false
    let snapPoint: LL | null = null
    if (this.snapTrails) {
      if (!this.previewFeatures) this.previewFeatures = this.gatherTrailFeatures()
      if (!this.previewGraph) this.previewGraph = buildTrailGraph(this.previewFeatures)
      const r = previewRoute(
        this.previewGraph,
        [lastPoint.lng, lastPoint.lat],
        [mousePos.lng, mousePos.lat],
        this.snapTolMeters()
      )
      coords = r.coords
      cursorOnTrail = r.cursorOnTrail
      snapPoint = r.snapPoint
    }
    this.lastPreviewOnTrail = cursorOnTrail

    if (cursorOnTrail) {
      // On a track → bold green trail slice.
      this.applyPreviewCoords(coords, '#2e7d32', true)
      this.updateSnapIndicator(snapPoint)
    } else if (this.snapTerrain) {
      // Off-trail + terrain: DEM least-cost preview (async; testing how expensive
      // this is per move). Don't draw the straight segment here — it would flicker
      // over the DEM path every frame; the resolved DEM path is the preview.
      this.updateSnapIndicator(null)
      void this.renderDemPreview(lastPoint, mousePos)
    } else {
      // Freehand (or trail-snap off-trail) → thin gray straight segment.
      this.applyPreviewCoords(coords, '#666666', false)
      this.updateSnapIndicator(null)
    }
  }

  /** Set the mouse-preview line geometry + style (shared by trail/DEM/straight). */
  private applyPreviewCoords(coords: [number, number][], color: string, bold: boolean): void {
    if (!this.map || coords.length < 2) return
    const geojson: GeoFeature<GeoLineString> = {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coords },
      properties: {},
    }
    const source = this.map.getSource(this.mousePreviewSourceId)
    if (this.mousePreviewLayer && isGeoJSONSource(source)) {
      source.setData(geojson)
    } else {
      this.map.addSource(this.mousePreviewSourceId, { type: 'geojson', data: geojson })
      this.map.addLayer({
        id: this.mousePreviewLayerId,
        type: 'line',
        source: this.mousePreviewSourceId,
        paint: {
          'line-color': '#666666',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [4, 4],
        },
      })
      this.mousePreviewLayer = {
        sourceId: this.mousePreviewSourceId,
        layerId: this.mousePreviewLayerId,
      }
    }
    this.map.setPaintProperty(this.mousePreviewLayerId, 'line-color', color)
    this.map.setPaintProperty(this.mousePreviewLayerId, 'line-width', bold ? 3 : 2)
    this.map.setPaintProperty(this.mousePreviewLayerId, 'line-opacity', bold ? 0.95 : 0.6)
  }

  /**
   * Off-trail DEM least-cost preview to the cursor (experimental — testing per-move
   * cost). One request at a time; the next frame fires for the latest cursor, so it
   * keeps up as fast as the solver allows and lags the cursor by ~one solve.
   */
  private async renderDemPreview(
    last: maplibregl.LngLat,
    cursor: maplibregl.LngLat
  ): Promise<void> {
    if (!this.map) return
    const A: [number, number] = [last.lng, last.lat]
    const C: [number, number] = [cursor.lng, cursor.lat]
    // Dedup: never re-run A* for a cursor we've already solved (or are solving) this
    // leg. While a solve is in flight, drop new ones — the trailing edge below re-fires
    // for the latest position, so we converge on the newest cursor without queuing a
    // backlog of stale solves.
    if (this.lastDemCursor && sameLngLat(this.lastDemCursor, C)) return
    if (this.demPreviewBusy) return
    this.demPreviewBusy = true
    this.lastDemCursor = C
    try {
      const LB = LinePolygonDrawingManager.DEM_PREVIEW_LOOKBACK_M
      const prefix = this.lastDemPath
      // Reuse the frozen prefix when it's for this same leg (starts at the current
      // anchor) and the cursor hasn't jumped beyond the lookback since last solve.
      const reuse =
        !!prefix &&
        prefix.length >= 2 &&
        sameLngLat(prefix[0], A) &&
        haversineMeters(prefix[prefix.length - 1], C) <= LB

      let path: [number, number][] | null
      if (reuse && prefix) {
        const cut = spliceIndexBackFromEnd(prefix, LB)
        const tail = await demRouteWorker(prefix[cut], C)
        path = tail && tail.length >= 2 ? [...prefix.slice(0, cut + 1), ...tail.slice(1)] : null
      } else {
        // First solve for this leg, or the cursor jumped → full route.
        path = await demRouteWorker(A, C)
      }

      // Apply only if still relevant: drawing, terrain on, and not now over a trail
      // (where the trail preview owns the render).
      if (
        !path ||
        path.length < 2 ||
        !this.isDrawing ||
        !this.snapTerrain ||
        this.lastPreviewOnTrail
      )
        return
      this.lastDemPath = path
      this.applyPreviewCoords(path, '#b45309', true)
    } finally {
      this.demPreviewBusy = false
      // Trailing edge: if the cursor moved on during the solve (including stopping at a
      // new spot, where no further mousemove fires), re-render for the latest position.
      const p = this.pendingMousePos
      if (p && this.isDrawing && this.snapTerrain && !sameLngLat([p.lng, p.lat], C))
        this.updateMousePreview(p)
    }
  }

  /** Show/move a small circle at the point the cursor will snap to (or hide it). */
  private updateSnapIndicator(snapPoint: LL | null): void {
    if (!this.map) return
    if (!snapPoint) {
      this.snapIndicator?.remove()
      this.snapIndicator = undefined
      return
    }
    if (!this.snapIndicator) {
      const el = document.createElement('div')
      el.style.width = '14px'
      el.style.height = '14px'
      el.style.borderRadius = '50%'
      el.style.background = 'rgba(46,125,50,0.35)'
      el.style.border = '2px solid #2e7d32'
      el.style.boxShadow = '0 0 0 2px rgba(255,255,255,0.6)'
      el.style.pointerEvents = 'none'
      this.snapIndicator = new maplibregl.Marker({ element: el, anchor: 'center' })
      this.snapIndicator.setLngLat(snapPoint).addTo(this.map)
    } else {
      this.snapIndicator.setLngLat(snapPoint)
    }
  }

  cleanupTempDrawing(): void {
    try {
      if (this.map) {
        if (this.map.getLayer(this.tempDrawingFillLayerId)) {
          this.map.removeLayer(this.tempDrawingFillLayerId)
        }
        if (this.map.getLayer(this.tempDrawingStrokeLayerId)) {
          this.map.removeLayer(this.tempDrawingStrokeLayerId)
        }
        if (this.map.getSource(this.tempDrawingSourceId)) {
          this.map.removeSource(this.tempDrawingSourceId)
        }
      }
    } catch {
      // Map style may already be destroyed during teardown
    }
    this.tempDrawingLayer = undefined

    // Clean up drawing handles
    if (this.drawingHandles) {
      this.drawingHandleMarkers.forEach(marker => marker.remove())
      this.drawingHandleMarkers = []
    }
    this.drawingHandles = undefined

    // Clean up mouse preview layer
    try {
      if (this.map) {
        if (this.map.getLayer(this.mousePreviewLayerId)) {
          this.map.removeLayer(this.mousePreviewLayerId)
        }
        if (this.map.getSource(this.mousePreviewSourceId)) {
          this.map.removeSource(this.mousePreviewSourceId)
        }
      }
    } catch {
      // Map style may already be destroyed during teardown
    }
    this.mousePreviewLayer = undefined

    // Hover-preview teardown
    this.snapIndicator?.remove()
    this.snapIndicator = undefined
    if (this.previewRaf !== null) {
      cancelAnimationFrame(this.previewRaf)
      this.previewRaf = null
    }
    this.previewFeatures = null
    this.previewGraph = null
    this.sessionLines.clear()
    this.pendingMousePos = null

    this.drawingPoints = []
    this.routedGaps = []
    this.clearHistory()
    this.extendingFeatureId = null
    this.extendReversed = false
    this.extendAwaitingEnd = false
    this.lastDemPath = null
    this.lastDemCursor = null
    this.followStrategy = null
    this.snapTrails = false
    this.snapTerrain = false
    this.snapUserSet = false
    this.draftFeatureId = null
    this.measureMode = false
    this.isDrawing = false
    this.isFinishing = false
  }

  completeOrCancelCurrentDrawing(previousTool: string): void {
    log.debug(`🔄 Switching from ${previousTool} tool - completing/canceling current drawing`)

    // Measure: a finished measure (not drawing) keeps its temp line as the open
    // View's visual — that's cleared on View dismiss. An in-progress measure is
    // discarded on a tool switch like any other incomplete shape.
    if (this.measureMode) {
      if (this.isDrawing) this.cleanupTempDrawing()
      return
    }

    if (this.isDrawing && this.drawingPoints.length > 0) {
      switch (previousTool) {
        case 'line':
          if (this.drawingPoints.length >= 2) {
            log.debug('✅ Auto-completing line with', this.drawingPoints.length, 'points')
            void this.finishLineDrawing()
          } else {
            log.debug('🚫 Canceling incomplete line (need at least 2 points)')
            this.cleanupTempDrawing()
          }
          break

        case 'polygon':
          if (this.drawingPoints.length >= 3) {
            log.debug('✅ Auto-completing polygon with', this.drawingPoints.length, 'points')
            void this.finishPolygonDrawing()
          } else {
            log.debug('🚫 Canceling incomplete polygon (need at least 3 points)')
            this.cleanupTempDrawing()
          }
          break

        case 'point':
          this.cleanupTempDrawing()
          break

        default:
          log.debug('🚫 Canceling unknown drawing type:', previousTool)
          this.cleanupTempDrawing()
          break
      }
    } else {
      this.cleanupTempDrawing()
    }
  }

  dispose(): void {
    this.cleanupTempDrawing()
  }
}
